import type sv from '../sv/inbox'

const inbox: typeof sv = {
  title: 'Inbox',
  address: {
    label: 'Project email address',
    copy: 'Copy address',
    copied: 'Address copied',
    addContact: 'Add as contact',
    hint: 'Mail sent here lands in the inbox. Rules decide what HUB does with it.',
  },
  contact: {
    title: 'Add as contact',
    hint: 'Scan with your phone camera to save the project as the contact "{{name}}".',
    download: 'Download contact card (.vcf)',
    qrAlt: 'QR code with the project contact card',
    loading: 'Generating QR code...',
  },
  folders: {
    inbox: 'Inbox',
    processed: 'Processed',
    archive: 'Archive',
  },
  rulesLink: 'Rules',
  searchPlaceholder: 'Search the inbox...',
  empty: {
    inbox: 'No emails in the inbox',
    processed: 'No processed emails yet',
    archive: 'The archive is empty',
    search: 'No emails match your search',
  },
  noSubject: '(no subject)',
  disabled: {
    title: 'The inbox is not enabled',
    description:
      'The project inbox is not turned on for your organization. An administrator can enable it.',
    back: 'Back to the project',
  },
  select: 'Select an email to read it',
  loading: 'Loading...',
  status: {
    new: 'New',
    no_match: 'No rule',
    matched: 'Rule matched',
    processing: 'Processing',
    done: 'Done',
    failed: 'Failed',
  },
  detail: {
    from: 'From',
    to: 'To',
    cc: 'Cc',
    received: 'Received',
    attachments: 'Attachments',
    download: 'Download',
    preview: 'View {{name}}',
    openPdf: 'Open the PDF',
    closePreview: 'Close',
    saveToDocuments: 'Save to Documents',
    saved: 'Saved to Documents: {{title}}',
    alreadySaved: 'The attachment is already in Documents: {{title}}',
    openDocument: 'Open in Documents',
    saveFailed: 'Could not save the attachment',
    copyFrom: 'Copy the sender address',
    copiedFrom: 'Address copied',
    askAboutSelection: 'Ask HUB',
    emptyBody: '(empty message)',
    error: 'Error',
  },
  body: {
    showQuoted: 'Show quoted text',
    hideQuoted: 'Hide quoted text',
    forwarded: 'Forwarded message',
    fwdFrom: 'From',
    fwdDate: 'Date',
    fwdTo: 'To',
    fwdSubject: 'Subject',
    imagesBlocked_one: '{{count}} image from the sender is blocked',
    imagesBlocked_other: '{{count}} images from the sender are blocked',
    loadImages: 'Show images',
    showText: 'View as text',
    showHtml: 'View as HTML',
    htmlTooLarge: 'This email is too large to show with formatting. Here is the text version.',
    frameTitle: 'Email content',
  },
  runs: {
    title: 'Runs',
    rule: 'Rule: {{name}}',
    manual: 'Manual request',
    openReply: 'Show HUB’s reply in the chat',
    status: {
      queued: 'Queued',
      running: 'Running',
      done: 'Done',
      failed: 'Failed',
    },
  },
  actions: {
    runRule: 'Run rule',
    runRuleMoves: 'Moves the mail to Processed after running',
    delete: 'Delete',
    archive: 'Archive',
    moveToInbox: 'Move to inbox',
    markProcessed: 'Mark as processed',
    noRules: 'No rules yet',
    manageRules: 'Manage rules',
  },
  ask: {
    started: 'HUB is working on the email',
  },
  bubble: {
    title: 'HUB',
    open: 'Open HUB',
    collapse: 'Minimise',
    hint: 'Ask HUB about the email – reply, file attachments, create tasks…',
    placeholder: 'Ask HUB about the email or request an action…',
  },
  delete: {
    title: 'Delete the email?',
    description:
      'The email, its attachments and HUB’s chat about it are removed for good. Documents saved from the attachments stay.',
    cancel: 'Cancel',
    confirm: 'Delete',
    done: 'Email deleted',
    failed: 'Could not delete the email',
    processing: 'HUB is still working on the email – wait until the run has finished.',
  },
  credits: {
    exhausted: 'Credits are exhausted – the run could not be started.',
  },
  rules: {
    title: 'Inbox rules',
    description:
      'Rules are tried top to bottom when an email arrives. The first matching rule starts HUB with its instruction.',
    back: 'Back to the inbox',
    create: 'New rule',
    empty: 'No rules yet. Create a rule to let HUB handle incoming email automatically.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    moveUp: 'Move up',
    moveDown: 'Move down',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete the rule?',
    deleteDescription: '"{{name}}" will be removed. Runs already started are not affected.',
    conditionsSummary_one: '{{count}} condition',
    conditionsSummary_other: '{{count}} conditions',
    matchAll: 'all must match',
    matchAny: 'any must match',
    autoArchiveOn: 'Moved to Processed after the run',
    editor: {
      createTitle: 'New rule',
      editTitle: 'Edit rule',
      name: 'Name',
      namePlaceholder: 'E.g. Supplier invoices',
      enabled: 'Rule is enabled',
      conditions: 'Conditions',
      matchMode: 'Run when',
      matchAll: 'all conditions match',
      matchAny: 'any condition matches',
      addCondition: 'Add condition',
      removeCondition: 'Remove condition',
      valuePlaceholder: 'Value',
      prompt: 'Instruction for HUB',
      promptPlaceholder:
        'E.g. Save the attachment to Documents, summarise the invoice and create a task for approval.',
      promptHint:
        'HUB gets the email as context and can read attachments, save them to Documents, create tasks and reply to the email.',
      autoArchive: 'Move the email to Processed when the run finishes',
      test: 'Test against recent emails',
      testResult_one: 'Matches {{count}} of the last {{total}} emails',
      testResult_other: 'Matches {{count}} of the last {{total}} emails',
      testEmpty: 'None of the recent emails match',
      save: 'Save',
      cancel: 'Cancel',
      saved: 'Rule saved',
      validation: 'Fill in a name, at least one condition with a value and an instruction.',
    },
    fields: {
      from: 'Sender',
      subject: 'Subject',
      body: 'Message',
      to: 'Recipient',
      has_attachment: 'Has attachment',
      attachment_name: 'Attachment name',
    },
    ops: {
      contains: 'contains',
      equals: 'is exactly',
      starts_with: 'starts with',
      ends_with: 'ends with',
      is: 'is',
    },
    bool: {
      true: 'Yes',
      false: 'No',
    },
  },
}

export default inbox
